/**
 * Header Component
 * For Media Devoted | Created By Ciro Autiero
 */

import { useMemo } from 'react';
import { useLocation } from 'react-router-dom';
import { LogOut, User, PanelLeftClose, PanelLeftOpen, Moon, Sun } from 'lucide-react';
import * as DropdownMenu from '@radix-ui/react-dropdown-menu';

import { useAuth } from '@/hooks';
import { useTheme } from '@/providers/ThemeProvider';
import { usePinnedCountries } from '@/hooks';
import { useCountryPinOrder } from '@/hooks';
import { Button } from '@/components/ui';
import { cn } from '@/lib/utils';
import { PinnedCountriesStrip } from '@/components/layouts/pinned-strip';

export function Header({
  sidebarCollapsed,
  onToggleSidebar,
}: {
  sidebarCollapsed: boolean;
  onToggleSidebar: () => void;
}) {
  const { user, logout } = useAuth();
  const { setTheme, resolvedTheme } = useTheme();
  const location = useLocation();
  const pinnedCountries = usePinnedCountries(!!user);
  const { ordered, setOrder } = useCountryPinOrder(pinnedCountries.data ?? []);

  const isSmswiserPage = useMemo(
    () => location.pathname.startsWith('/smswiser') || location.pathname === '/calendar',
    [location.pathname],
  );

  const canShowPinnedCountries = Boolean(user && isSmswiserPage && ordered.length > 0);

  return (
    <header className="relative flex h-16 items-center justify-between border-b bg-card px-6">
      <div className="flex items-center gap-3">
        <Button
          variant="ghost"
          size="icon"
          onClick={onToggleSidebar}
          aria-label={sidebarCollapsed ? 'Expand sidebar' : 'Collapse sidebar'}
          title={sidebarCollapsed ? 'Expand sidebar' : 'Collapse sidebar'}
          className="hidden md:inline-flex"
        >
          {sidebarCollapsed ? <PanelLeftOpen className="h-5 w-5" /> : <PanelLeftClose className="h-5 w-5" />}
        </Button>
      </div>

      {canShowPinnedCountries && <PinnedCountriesStrip ordered={ordered} setOrder={setOrder} />}

      <div className="flex items-center gap-4">

        <Button
          variant="ghost"
          size="icon"
          onClick={() => setTheme(resolvedTheme === 'dark' ? 'light' : 'dark')}
        >
          {resolvedTheme === 'dark' ? <Sun className="h-5 w-5" /> : <Moon className="h-5 w-5" />}
        </Button>

        <DropdownMenu.Root>
          <DropdownMenu.Trigger asChild>
            <Button variant="ghost" className="flex items-center gap-2">
              <div className="flex h-8 w-8 items-center justify-center rounded-full bg-primary text-primary-foreground">
                <User className="h-4 w-4" />
              </div>
              <span className="hidden sm:inline">
                {user?.firstName && user?.lastName
                  ? `${user.firstName} ${user.lastName}`
                  : user?.email?.split('@')[0]}
              </span>
            </Button>
          </DropdownMenu.Trigger>

          <DropdownMenu.Portal>
            <DropdownMenu.Content
              className={cn(
                'z-50 min-w-[200px] rounded-md border bg-popover p-1 shadow-md',
                'data-[state=open]:animate-in data-[state=closed]:animate-out',
                'data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0',
                'data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95',
              )}
              sideOffset={5}
              align="end"
            >
              <div className="border-b px-2 py-1.5">
                <p className="text-sm font-medium">{user?.email}</p>
                <p className="text-xs text-muted-foreground capitalize">{user?.role}</p>
              </div>

              <DropdownMenu.Item
                className="relative flex cursor-pointer select-none items-center gap-2 rounded-sm px-2 py-1.5 text-sm outline-none transition-colors hover:bg-accent hover:text-accent-foreground"
                onClick={logout}
              >
                <LogOut className="h-4 w-4" />
                Sign out
              </DropdownMenu.Item>
            </DropdownMenu.Content>
          </DropdownMenu.Portal>
        </DropdownMenu.Root>
      </div>
    </header>
  );
}
